import {gql} from '@apollo/client'
import {CampaignType} from '../../__generated__/graphql'

export const isNativeDisplayCampaign = (type: CampaignType) =>
  [
    CampaignType.MarketplaceNativeDisplay,
    CampaignType.InHouseNativeDisplay,
    CampaignType.ContentRecirculation,
  ].includes(type)

const campaign = `
  _id
  name
  type
  marketplace
  disableSEO
  actions {
    action
    disabled
    disabledReason
  }
  country
  targetValue
  targetType
  optimisation
  startDate
  endDate
  everActivated
  pacing
  priority
  landingPage
  sensitiveCategory
  policyUrl
  sponsor
  status
  dailyBudgetLimit
  advertiserId
  advertiser {
    name
    image
  }
  trackingTag {
    tagType
    tagUrl
  }
  publishers {
    name
  }
  contentSource
  content {
    headline
    articleByline
    teaser
    imageByline
    coverImage
    contentObject
    contentImages
  }
  publishers {
    _id,
    name
  }
  websites {
    _id
    name
    allowSelfHostedAdvertorials
    cpcFloorPrice
    cprFloorPrice
    floorPriceExemptCompanyIds
  }
  creatorId
  ownerCompanyId
  publications {
    _id
    websiteId
    previewUrl
    website {
      _id
      name
      allowSelfHostedAdvertorials
    }
    publisher {
      _id
      name
    }
    selfHostedContent {
      url
      cmsArticleId
    }
    providerContent {
      providerContentId
    }
  }
  teasers {
    _id
    status
    headline
    imageId
    websites {
      _id
      name
    }
  }
  rateCard {
    priceModel
    price
  }
  ecpmOptimization
  targeting {
    consentStatus
    deviceType
    dayOfWeek
    timeRange {
      timeFrom
      timeTo
    }
    location {
      countryCode
      region
      city
    }
    mobileCarrier
    sections
  }
  labels {
    _id
    name
  }
  billingId
  dealId
`

export const CREATE_CAMPAIGN = gql`
  mutation CampaignCreateOne($record: CreateCampaignInput!) {
    CampaignCreateOne(record: $record) {
      _id
      marketplace
      type
      contentSource
      billingId
    }
  }
`

export const GET_CAMPAIGN = gql`
  query GetCampaign($campaignId: ObjectId!) {
    campaign: CampaignById(_id: $campaignId) {
      ${campaign}
    }
  }
`

const shortCampaign = `
  _id
  name
  type
  marketplace
  startDate
  endDate
  createdAt
  status
  publisherIds
  ownerCompanyId
  ownerCompany {
    name
  }
  everActivated
  publishers {
    _id
    name
  }
  advertiser {
    name
  }
  publications {
    status
    _id
    website {
        name
        _id
    }
    publisher {
        name
    }
  }
  targetValue
  targetType
  dailyBudgetLimit
  labels {
    name
    _id
  }
`
export const GET_ALL_CAMPAIGNS = gql`
query CampaignMany($page: Int, $perPage: Int, $sort: SortInput, $filters: CampaignFilterArgs) {
  campaigns: CampaignMany(page: $page, perPage: $perPage, sort: $sort, filters: $filters) {
    items {
      ${shortCampaign}
    }
    meta {
      totalPages
      totalItems
    }
  }
}
`
export const GET_CAMPAIGNS_WAITING = gql`
  query CampaignsWaiting {
    campaigns: CampaignsWaiting {
      ${shortCampaign}
    }
  }
`

export const GET_ALL_CAMPAIGNS_BILLING = gql`
  query GetCampaigns($filters: CampaignFilterArgs, $perPage: Int) {
    campaigns: CampaignMany(filters: $filters, perPage: $perPage) {
      items {
        _id
        name
        ownerCompanyId
        publishers {
          _id
          name
        }
        advertiser {
          name
        }
        publications {
          website {
            _id
            url
            name
          }
        }
        targetValue
      }
    }
  }
`

export const UPDATE_CAMPAIGN = gql`
  mutation CampaignUpdateById($id: ObjectId!, $record: UpdateCampaignInput!) {
    campaign: CampaignUpdateById(_id: $id, record: $record) {
      ${campaign}
    }
  }
`

export const PUBLISH_CAMPAIGN = gql`
  mutation CampaignPublishPublications(
    $campaignId: ObjectId!
    $publications: [PublicationPublishInput!]!
  ) {
    CampaignPublishPublications(
      campaignId: $campaignId
      publications: $publications
    ) {
      _id
      status
      campaign {
        _id
        status
      }
    }
  }
`

export const DELETE_CAMPAIGN = gql`
  mutation CampaignDeleteById($id: ObjectId!) {
    CampaignDeleteById(_id: $id)
  }
`

export const CLONE_CAMPAIGN = gql`
  mutation CampaignClone($campaignId: ObjectId!) {
    CampaignClone(campaignId: $campaignId) {
      _id
    }
  }
`

// TODO: Use CampaignActionType instead of String?
export const RUN_CAMPAIGN_ACTION = gql`
  mutation RunCampaignAction($action: String!, $campaignId: ObjectId!) {
    RunCampaignAction(action: $action, campaignId: $campaignId) {
      ${campaign}
    }
  }
`

export const GET_REGIONS = gql`
  query ($countryCode: String!) {
    regions: TargetingRegions(countryCode: $countryCode) {
      key
      value
    }
  }
`

export const GET_CITIES = gql`
  query ($countryCode: String!, $region: String!) {
    cities: TargetingCities(region: $region, countryCode: $countryCode) {
      value: key
      label: value
    }
  }
`

export const GET_LABELS = gql`
  query LabelMany($page: Int, $perPage: Int) {
    labels: LabelMany(page: $page, perPage: $perPage) {
      items {
        _id
        name
        configuration
        createdAt
      }
    }
  }
`

export const CREATE_LABEL = gql`
  mutation LabelCreateOne($record: CreateLabelInput!) {
    label: LabelCreateOne(record: $record) {
      _id
      name
      configuration
      createdAt
    }
  }
`

export const UPDATE_LABEL = gql`
  mutation LabelUpdateById($_id: ObjectId!, $record: UpdateLabelInput!) {
    label: LabelUpdateById(_id: $_id, record: $record) {
      _id
      name
      configuration
      createdAt
    }
  }
`
